import React, { useState } from 'react';
import { 
  ChevronDown, ChevronUp, CheckCircle, Info, 
  Code, Brain, MessageSquare, Clock, ShieldAlert 
} from 'lucide-react';

const ICONS = {
  "Coding Skills": Code,
  "Problem Solving": Brain,
  "Communication": MessageSquare,
  "Time Management": Clock,
  "Confidence": ShieldAlert,
};

const GradingPolicySection = ({ gradingPolicy, checkpointScores, parameters }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!gradingPolicy) return null;

  return (
    <div className="bg-dark/40 border border-white/10 rounded-xl overflow-hidden mt-6">
      <button 
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="bg-primary/20 p-2 rounded-lg">
            <Info className="w-5 h-5 text-primary" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-white">How is this scored?</h3>
            <p className="text-sm text-gray-400">View the deterministic grading policy and weights</p>
          </div>
        </div>
        {isExpanded ? <ChevronUp className="text-gray-400" /> : <ChevronDown className="text-gray-400" />}
      </button>

      {isExpanded && (
        <div className="p-6 border-t border-white/10 space-y-8 animate-in fade-in slide-in-from-top-2 duration-300">
          
          {/* Category Weights Breakdown */}
          <div>
            <h4 className="text-sm font-medium text-gray-400tracking-wider mb-4">Score Weighting Formula</h4>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              {gradingPolicy.categoryWeights.map((cat, idx) => {
                const Icon = ICONS[cat.name] || Info;
                const param = parameters?.find(p => p.name === cat.name);
                const score = param ? param.score : 0;
                const pointsEarned = (score * cat.weight).toFixed(1);

                return (
                  <div key={idx} className="bg-white/5 border border-white/10 p-3 rounded-lg flex flex-col items-center text-center">
                    <Icon className="w-5 h-5 text-gray-400 mb-2" />
                    <span className="text-xs font-medium text-gray-300 mb-1">{cat.name}</span>
                    <span className="text-lg font-bold text-white mb-1">{cat.percentage}%</span>
                    <div className="text-[10px] text-gray-500 bg-black/20 px-2 py-1 rounded w-full mt-auto">
                      {score} × {cat.weight} = <span className="text-primary font-medium">{pointsEarned} pts</span>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Final Calculation Bar */}
            <div className="mt-4 bg-black/30 p-3 rounded-lg flex items-center justify-between border border-white/5">
              <span className="text-sm text-gray-400">Total Calculation:</span>
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-gray-300 tracking-wider">
                  {gradingPolicy.categoryWeights.map(cat => {
                    const param = parameters?.find(p => p.name === cat.name);
                    return `${param ? param.score : 0}×${cat.weight}`;
                  }).join(' + ')}
                </span>
                <span className="text-gray-500">=</span>
                <span className="text-white font-bold">
                  {parameters?.reduce((acc, p) => {
                    const weight = gradingPolicy.categoryWeights.find(c => c.name === p.name)?.weight || 0;
                    return acc + (p.score * weight);
                  }, 0).toFixed(0)} / 100
                </span>
              </div>
            </div>
          </div>

          {/* Checkpoint Rubric */}
          {checkpointScores && (
            <div>
              <h4 className="text-sm font-medium text-gray-400tracking-wider mb-4">Problem Solving Checklist</h4>
              <p className="text-xs text-gray-500 mb-4">The Problem Solving score is strictly determined by completing these interview checkpoints. Partial completion yields 50% points.</p>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs text-gray-400 bg-white/5">
                    <tr>
                      <th className="px-4 py-3 rounded-tl-lg">Checkpoint</th>
                      <th className="px-4 py-3 text-center">Max Points</th>
                      <th className="px-4 py-3 text-center">Status</th>
                      <th className="px-4 py-3 text-right rounded-tr-lg">Earned</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {checkpointScores.checkpoints.map((chk, idx) => (
                      <tr key={idx} className="hover:bg-white/5">
                        <td className="px-4 py-3 font-medium text-gray-300">
                          {chk.displayName}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-500">{chk.maxPoints}</td>
                        <td className="px-4 py-3 text-center">
                          {chk.status === "done" && <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/10 text-green-400 border border-green-500/20"><CheckCircle className="w-3 h-3" /> Done</span>}
                          {chk.status === "partial" && <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-500/10 text-yellow-400 border border-yellow-500/20"><div className="w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin-slow" /> Partial</span>}
                          {chk.status === "pending" && <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-white/5 text-gray-500 border border-white/10"><div className="w-1.5 h-1.5 rounded-full bg-gray-500" /> Pending</span>}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-gray-300">
                          <span className={chk.earnedPoints > 0 ? "text-primary" : "text-gray-500"}>{chk.earnedPoints}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-black/20">
                    <tr>
                      <td colSpan="3" className="px-4 py-3 text-right font-medium text-gray-400 rounded-bl-lg">Total Problem Solving Score</td>
                      <td className="px-4 py-3 text-right font-bold text-white rounded-br-lg">{checkpointScores.rawScore} / 100</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Score Anchors */}
          <div>
            <h4 className="text-sm font-medium text-gray-400tracking-wider mb-4">Verdict Scale</h4>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {gradingPolicy.scoreAnchors.map((anchor, idx) => (
                <div key={idx} className="bg-white/5 border border-white/5 p-3 rounded text-center">
                  <div className="text-lg font-bold text-white">{anchor.range}</div>
                  <div className="text-xs text-gray-400 mt-1">{anchor.verdict}</div>
                </div>
              ))}
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default GradingPolicySection;
